import { useNavigate, useParams } from 'react-router-dom';
import CustomHistory from '../components/customHistory/CustomHistorySection';
import Footer from '../components/Footer/FooterSection';
import HeaderTwo from '../components/HeaderTwo/HeaderTwoSection';
import '../styles/order.scss';
import { BoxIcon, Hourglass, XCircle } from 'lucide-react';
import RenderCartProp from '../components/RenderCartProp/RenderCartProp';
import { useEffect, useMemo, useState } from 'react';
import { useNotification } from '../context/NotificationContext';
import { getOrder } from '../api/requests';
import SkeletonLoader from '../components/SkeletonLoaders/DefaultLoader';
import { OrderShow } from '../interface/interfaces';
import 'animate.css';
import ReturnModal from '../components/ReturnModal/ReturnModal';

const Order = () => {
 const navigate = useNavigate();
 const { orderid } = useParams();
 const { notify } = useNotification();

 type pageProp = {
  orderData: OrderShow | null;
  isLoading: boolean;
 };

 const [pageData, setPageData] = useState<pageProp>({
  orderData: null,
  isLoading: false,
 });

 const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);

 const setPageLoader = (value: boolean) => {
  setPageData((prev) => {
   return {
    ...prev,
    isLoading: value,
   };
  });
 };

 useEffect(() => {
  const func = async () => {
   if (!orderid) {
    notify.error('Invalid link, please reload!');
    return;
   }

   try {
    setPageLoader(true);
    let result = await getOrder(orderid);

    if (result) {
     setPageData((prev) => {
      return {
       ...prev,
       orderData: result,
      };
     });
    } else {
     notify.error('Order not found!');
    }
   } catch (error: any) {
    notify.error(error.message);
   } finally {
    setPageLoader(false);
   }
  };

  func();
  // eslint-disable-next-line
 }, []);

 const dateFunc = (rawDate: string): string => {
  const date = new Date(rawDate);

  const opts: Intl.DateTimeFormatOptions = {
   year: 'numeric',
   month: 'long',
   day: 'numeric',
  };

  const formatted = date.toLocaleDateString('en-US', opts);
  return formatted;
 };

 const colorFunc = (
  text: string,
 ): 'green' | 'yellow' | 'red' | 'default' => {
  const normalized = text.trim().toLowerCase();

  const colorMap: Record<'green' | 'yellow' | 'red', string[]> = {
   green: [
    'created',
    'ready',
    'delivered',
    'picked_up',
    'picked up',
    'dispatched',
    'assigned to a rider',
   ],
   yellow: [
    'awaiting vendor confirmation',
    'awaitingconfirmation',
    'pending pick-up',
    'pendingpickup',
    'returned',
   ],
   red: ['cancelled'],
  };

  for (const [color, keywords] of Object.entries(colorMap) as [
   keyof typeof colorMap,
   string[],
  ][]) {
   if (keywords.some((k) => normalized === k)) {
    return color;
   }
  }

  return 'default';
 };

 const statusMessage = (text: string): string => {
  const phrase = text.trim().toLowerCase();

  const messages: Record<string, string> = {
   created: 'Order has been created',
   ready: 'Order is ready for processing',
   delivered: 'Order has been delivered',
   'awaiting vendor confirmation': 'Waiting for vendor confirmation',
   awaitingconfirmation: 'Waiting for vendor confirmation',
   'pending pick-up': 'Order is waiting to be picked up',
   pendingpickup: 'Order is waiting to be picked up',
   'picked up': 'Order has been picked up',
   picked_up: 'Order has been picked up',
   dispatched: 'Order is on its way',
   returned: 'Order was returned',
   cancelled: 'Order has been cancelled',
  };

  return messages[phrase] || 'Status update not available';
 };

 const isTxSuccess = (status: string) => {
  let raw_text = status.toLowerCase();

  if (raw_text === 'success') {
   return true;
  } else {
   return false;
  }
 };

 const handleTrack = (order_id: string, track_id: string) => {
  navigate(`/tracking/${order_id}/${track_id}`);
 };

 const itemsTotal = useMemo(
  () =>
   pageData.orderData?.products?.reduce(
    (sum, p) => sum + p.amount,
    0,
   ) ?? 0,
  [pageData.orderData?.products],
 );

 const pluralRender = (letter: string, number: number) => {
  if (number > 1) {
   return letter;
  } else {
   return '';
  }
 };

 return (
  <>
   <HeaderTwo></HeaderTwo>

   <CustomHistory></CustomHistory>

   <div className="content order">
    {pageData.isLoading && (
     <SkeletonLoader count={4}></SkeletonLoader>
    )}

    {!pageData.isLoading && pageData.orderData && (
     <div className="animate__animated animate__fadeIn animate__fast">
      <div className="new_header_container">
       <div className="pagetitle">
        <div
         className="icon goback"
         onClick={() => navigate(-1)}
        ></div>
        <div>Order details</div>
       </div>

       {pageData.orderData.status?.toLocaleLowerCase() ===
        'delivered' && (
        <div className="action_area">
         <button
          className="btn full"
          onClick={() => setIsReturnModalOpen(true)}
         >
          Return Order
         </button>
        </div>
       )}
      </div>

      <div className="orderdet">
       {isTxSuccess(pageData.orderData.transaction_status) && (
        <>
         {colorFunc(pageData.orderData.status) === 'green' && (
          <div className="statusicon green">
           <div className="checkmark"></div>
          </div>
         )}

         {colorFunc(pageData.orderData.status) === 'yellow' && (
          <div className="statusicon yellow">
           <Hourglass
            size={22}
            strokeWidth={1.5}
           ></Hourglass>
          </div>
         )}

         {colorFunc(pageData.orderData.status) === 'red' && (
          <div className="statusicon red">
           <XCircle
            size={22}
            strokeWidth={1.5}
           ></XCircle>
          </div>
         )}
        </>
       )}

       {!isTxSuccess(pageData.orderData.transaction_status) && (
        <>
         <div className="statusicon yellow">
          <Hourglass
           size={22}
           strokeWidth={1.5}
          ></Hourglass>
         </div>
        </>
       )}

       <div className="orderinfo">
        <div className="ordertitle">
         Order{' '}
         <span className="cap">{pageData.orderData.order_id}</span>
        </div>
        <div className="ordertxt">
         Placed on {dateFunc(pageData.orderData.order_date)}
        </div>
        <div className="ordertxt">
         {pageData.orderData.products.length} item
         {pluralRender('s', pageData.orderData.products.length)}
        </div>
        {isTxSuccess(pageData.orderData.transaction_status) && (
         <>
          <div className="statustext">
           {statusMessage(pageData.orderData.status)}
          </div>
         </>
        )}
        {!isTxSuccess(pageData.orderData.transaction_status) && (
         <>
          <div className="statustext">Waiting for payment</div>
         </>
        )}
       </div>
      </div>

      <div className="title">Order items</div>

      <div className="orderbox">
       {pageData.orderData.products.map((data, idx) => (
        <div
         className="eachbox"
         key={idx}
        >
         <div className="product-info">
          <div className="imageBox">
           {data.product_image.length >= 1 && (
            <img
             src={data.product_image[0]}
             className="image"
             alt="Product"
            ></img>
           )}
           {data.product_image.length <= 0 && (
            <BoxIcon
             size={40}
             strokeWidth={1}
             color="var(--gray-one)"
            ></BoxIcon>
           )}
          </div>

          <div className="textcontent">
           <div className="productname">{data.product_name}</div>

           <div className="props">
            <div className="eachprop">
             <span className="proptitle">QTY:</span>
             <span className="propvalue">{data.quantity}</span>
            </div>

            {data.selectedProperties?.map((data, idx) => (
             <div
              className="eachprop"
              key={idx}
             >
              <span className="proptitle">{data.name}:</span>
              <RenderCartProp
               type={data.type}
               value={data.value}
              ></RenderCartProp>
             </div>
            ))}
           </div>

           <div className="price">
            ₦{data.amount.toLocaleString()}
           </div>
          </div>
         </div>

         <div className="action_area">
          <button
           className="btn full"
           onClick={() =>
            handleTrack(pageData.orderData?.order_id!, data.id)
           }
          >
           Track Item
          </button>
         </div>
        </div>
       ))}
      </div>

      <div className="twoGrid">
       <div className="eachgdetail payinfo">
        <div className="gheader">
         <div className="gicon paycard"></div>
         <div className="gtitle">Payment Summary</div>
        </div>

        <div className="gcontent">
         <div className="gdetails">
          <div className="title">Payment Method</div>
          <div className="text">
           Payment with {pageData.orderData.payment_method}
          </div>
         </div>

         <div className="gdetails table">
          <div className="title">Payment Details</div>

          <div className="tablearea">
           <div className="gdettab">
            <div className="key">Items total</div>
            <div className="value">
             ₦{itemsTotal.toLocaleString()}
            </div>
           </div>

           <div className="gdettab">
            <div className="key">Delivery fees:</div>
            <div className="value">
             ₦{pageData.orderData.delivery_price.toLocaleString()}
            </div>
           </div>

           {/* <div className="gdettab">
                                                    <div className="key">Discount Applied:</div>
                                                    <div className="value">$5.00</div>
                                                </div> */}

           <div className="gtotaltab">
            <div className="key">Total paid:</div>
            <div className="value">
             ₦
             {(
              itemsTotal + pageData.orderData.delivery_price
             ).toLocaleString()}
            </div>
           </div>
          </div>
         </div>
        </div>
       </div>

       <div className="eachgdetail deliveryinfo">
        <div className="gheader">
         <div className="gicon delicard"></div>
         <div className="gtitle">Delivery information</div>
        </div>

        <div className="gcontent">
         <div className="gdetails">
          <div className="title">Delivery Method</div>
          <div className="text">Door delivery</div>
         </div>

         <div className="gdetails table">
          <div className="title">Delivery Details</div>

          <div className="tablearea">
           <div className="gdettab">
            <div className="key">Name:</div>
            <div className="value">
             {pageData.orderData.customer_first_name}{' '}
             {pageData.orderData.customer_last_name}
            </div>
           </div>

           <div className="gdettab">
            <div className="key">Address:</div>
            <div className="value addr">
             {pageData.orderData.delivery_address}
            </div>
           </div>

           <div className="gdettab">
            <div className="key">Phone:</div>
            <div className="value">
             {pageData.orderData.customer_telephone_no}
            </div>
           </div>

           <div className="gdettab">
            <div className="key">Tracking ID:</div>
            <div className="value">{pageData.orderData.order_id}</div>
           </div>
           {/* 
                                    <div className="gtotaltab">
                                        <div className="key">Total paid:</div>
                                        <div className="value">$16.00</div>
                                    </div> */}
          </div>
         </div>
        </div>
       </div>
      </div>
     </div>
    )}
   </div>

   {pageData.orderData && (
    <ReturnModal
     isOpen={isReturnModalOpen}
     onClose={() => setIsReturnModalOpen(false)}
     orderId={pageData.orderData.id}
     maxQuantity={pageData.orderData.products.reduce(
      (acc, p) => acc + p.quantity,
      0,
     )}
    />
   )}

   <Footer></Footer>
  </>
 );
};

export default Order;
